import axios from 'axios';
import { toast } from 'sonner';
import { API, calculateNextDueDate, paiementsEnregistres } from './constants';

export default function useValidationActions(data) {
  const {
    _acomptesData, _comptaStats, _recusESS, _selectedAcompte, _selectedPaiement, _setAcomptesData,
    _setComptaStats, _setShowAcomptesTab, _showAcomptesTab, _showRecuEcheanceModal, _showStripeCBModal, acompteForm,
    activeTab, allDocuments, archiving, assignForm, bulkProcessing, calculateMontantHT,
    calculateMontantTTC, coopers, creatingEcheancier, dashboardStats, documentsEnAttente, documentsValides,
    echeanceForm, echeancierForm, experts, exportLoading, fetchData, filterDateDebut,
    filterDateFin, filterMontantMax, filterMontantMin, filterRecuStatus, filterType, filteredDocuments,
    generatingRecu, getAuthHeader, loading, loadingPartialPayments, members, partialPaymentData,
    partialPaymentsHistory, processingPartialPayment, refreshing, searchTerm, selectedDocument, selectedIds,
    selectedPaiementIndex, sepaModalEmail, sepaModalName, setAcompteForm, setActiveTab, setAllDocuments,
    setArchiving, setAssignForm, setBulkProcessing, setCoopers, setCreatingEcheancier, setEcheanceForm,
    setEcheancierForm, setExperts, setExportLoading, setFilterDateDebut, setFilterDateFin, setFilterMontantMax,
    setFilterMontantMin, setFilterRecuStatus, setFilterType, setGeneratingRecu, setLoading, setLoadingPartialPayments,
    setMembers, setPartialPaymentData, setPartialPaymentsHistory, setProcessingPartialPayment, setRecusESS, setRefreshing,
    setSearchTerm, setSelectedAcompte, setSelectedDocument, setSelectedIds, setSelectedPaiement, setSelectedPaiementIndex,
    setSepaModalEmail, setSepaModalName, setShareForm, setSharing, setShowAdvancedFilters, setShowAssignModal,
    setShowEcheanceModal, setShowEcheancierModal, setShowEditAcompteModal, setShowPartialPaymentModal, setShowRecuEcheanceModal, setShowShareModal,
    setShowStripeCBModal, setShowStripeSepaModal, setShowValidationModal, setStripeProcessing, setStripeSepaType, setUpdatingEcheance,
    setValidating, setValidationData, shareForm, sharing, showAdvancedFilters, showAssignModal,
    showEcheanceModal, showEcheancierModal, showEditAcompteModal, showPartialPaymentModal, showShareModal, showStripeSepaModal,
    showValidationModal, stats, stripeProcessing, stripeSepaType, updatingEcheance, validating,
    validationData,
  } = data;

  const openValidationModal = (doc) => {
    setSelectedDocument(doc);
    const montantTTC = calculateMontantTTC(doc);
    setValidationData({
      date_virement: new Date().toISOString().split('T')[0],
      montant_ttc: montantTTC.toFixed(2),
      reference_virement: '',
      observations: ''
    });
    setShowValidationModal(true);
  };

  // Valider le paiement
  const handleValidatePayment = async () => {
    if (!selectedDocument) return;
    
    setValidating(true);
    try {
      const isAvenant = selectedDocument.type_document === 'avenant' || selectedDocument.numero_police?.startsWith('AV-');
      
      const payload = {
        date_virement: validationData.date_virement,
        montant_ttc: parseFloat(validationData.montant_ttc),
        reference_virement: validationData.reference_virement,
        reference_paiement: validationData.reference_virement,
        mode_paiement: 'virement',
        observations: validationData.observations
      };
      
      if (paiementsEnregistres(selectedDocument).length > 0 && !selectedDocument.paiement_valide && !validationData.saisie_nouveau) {
        const r = await axios.post(`${API}/souscriptions/admin/${selectedDocument.id}/confirmer-paiements`, {}, { headers: getAuthHeader() });
        toast.success(r.data?.documents_signes
          ? 'Paiements confirmés — attestation et CP/CG signées avec QR code publiées'
          : 'Paiements confirmés — génération des documents signés à vérifier');
        setShowValidationModal(false);
        fetchData(true);
        return;
      }
      if (validationData.a_reclamer) {
        await axios.post(`${API}/souscriptions/admin/${selectedDocument.id}/validate-a-reclamer`,
          { date_limite: validationData.date_limite, motif: validationData.motif_reclamer }, { headers: getAuthHeader() });
        toast.success('Dossier validé sans encaissement — montant marqué « à réclamer »');
        setShowValidationModal(false);
        fetchData(true);
        return;
      }
      // Tous les documents (contrats et avenants) sont dans la collection souscriptions
      // Donc on utilise toujours l'endpoint souscriptions
      await axios.post(
        `${API}/souscriptions/admin/${selectedDocument.id}/validate-payment`,
        payload,
        { headers: getAuthHeader() }
      );
      
      toast.success(`✅ Paiement validé pour ${isAvenant ? 'l\'avenant' : 'la souscription'} !`);
      setShowValidationModal(false);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation du paiement');
    } finally {
      setValidating(false);
    }
  };

  // === FONCTIONS PAIEMENT PARTIEL ===
  
  // Ouvrir le modal de paiement partiel
  const openPartialPaymentModal = async (doc) => {
    setSelectedDocument(doc);
    setPartialPaymentData({
      montant: '',
      date_paiement: new Date().toISOString().split('T')[0],
      mode_paiement: 'virement',
      reference: '',
      observations: '',
      type_paiement: 'partiel',
      is_scheduled: false,
      scheduled_date: ''
    });
    setShowPartialPaymentModal(true);
    
    // Charger l'historique des paiements partiels
    await loadPartialPaymentsHistory(doc.id);
  };
  
  // Charger l'historique des paiements partiels
  const loadPartialPaymentsHistory = async (souscriptionId) => {
    setLoadingPartialPayments(true);
    try {
      const response = await axios.get(
        `${API}/souscriptions/admin/${souscriptionId}/partial-payments`,
        { headers: getAuthHeader() }
      );
      setPartialPaymentsHistory(response.data);
    } catch {
      setPartialPaymentsHistory({
        paiements: [],
        montant_total_ttc: 0,
        total_paye: 0,
        reste_a_payer: 0
      });
    } finally {
      setLoadingPartialPayments(false);
    }
  };
  
  // Enregistrer un paiement partiel ou acompte
  const handlePartialPayment = async () => {
    if (!selectedDocument) return;
    
    const montant = parseFloat(partialPaymentData.montant);
    if (!montant || montant <= 0) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }

    // Validation pour acompte programmé
    if (partialPaymentData.type_paiement === 'acompte' && partialPaymentData.is_scheduled && !partialPaymentData.scheduled_date) {
      toast.error('Veuillez saisir une date d\'encaissement pour l\'acompte programmé');
      return;
    }
    
    setProcessingPartialPayment(true);
    try {
      const isAcompte = partialPaymentData.type_paiement === 'acompte';
      
      const response = await axios.post(
        `${API}/souscriptions/admin/${selectedDocument.id}/partial-payment`,
        {
          montant: montant,
          date_paiement: partialPaymentData.date_paiement,
          mode_paiement: partialPaymentData.mode_paiement,
          reference: partialPaymentData.reference,
          observations: partialPaymentData.observations,
          type_paiement: partialPaymentData.type_paiement,
          is_acompte: isAcompte,
          is_scheduled: isAcompte ? partialPaymentData.is_scheduled : false,
          scheduled_date: isAcompte && partialPaymentData.is_scheduled ? partialPaymentData.scheduled_date : null
        },
        { headers: getAuthHeader() }
      );
      
      const { totaux, paiement } = response.data;
      const typeLabel = isAcompte ? 'Acompte' : 'Paiement partiel';
      
      if (totaux.est_solde) {
        toast.success(`✅ Paiement final enregistré ! La souscription est soldée.`);
        setShowPartialPaymentModal(false);
        fetchData(true);
      } else {
        const scheduledMsg = isAcompte && partialPaymentData.is_scheduled 
          ? ` (encaissement prévu le ${partialPaymentData.scheduled_date})`
          : '';
        toast.success(`✅ ${typeLabel} #${paiement.numero} enregistré (${montant.toFixed(2)} €)${scheduledMsg}. Reste à payer: ${totaux.reste_a_payer.toFixed(2)} €`);
        // Recharger l'historique
        await loadPartialPaymentsHistory(selectedDocument.id);
        // Reset le formulaire
        setPartialPaymentData({
          ...partialPaymentData,
          montant: '',
          reference: '',
          observations: '',
          is_scheduled: false,
          scheduled_date: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setProcessingPartialPayment(false);
    }
  };
  
  // === FONCTIONS GESTION DES ÉCHÉANCES ===
  
  // Ouvrir le modal de modification d'échéance
  const openEcheanceModal = (doc) => {
    setSelectedDocument(doc);
    setEcheanceForm({
      date_prochaine_echeance: doc.date_prochaine_echeance || calculateNextDueDate(doc) || '',
      observations: doc.echeance_observations || ''
    });
    setShowEcheanceModal(true);
  };
  
  // Modifier l'échéance
  const handleUpdateEcheance = async () => {
    if (!selectedDocument || !echeanceForm.date_prochaine_echeance) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    
    setUpdatingEcheance(true);
    try {
      await axios.put(
        `${API}/souscriptions/admin/${selectedDocument.id}/echeance`,
        echeanceForm,
        { headers: getAuthHeader() }
      );
      
      toast.success('Échéance mise à jour avec succès');
      setShowEcheanceModal(false);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingEcheance(false);
    }
  };
  
  // Ouvrir le modal d'échéancier
  const openEcheancierModal = (doc) => {
    setSelectedDocument(doc);
    const montantTotal = calculateMontantTTC(doc);
    const montantPaye = doc.montant_paye || doc.paiements_partiels?.reduce((s, p) => s + (p.amount || p.montant || 0), 0) || 0;
    const reste = montantTotal - montantPaye;
    
    // Pré-remplir avec des échéances mensuelles sur 3 mois
    const today = new Date();
    const echeances = [];
    const montantMensuel = Math.ceil(reste / 3);
    
    for (let i = 1; i <= 3; i++) {
      const date = new Date(today);
      date.setMonth(date.getMonth() + i);
      echeances.push({
        date: date.toISOString().split('T')[0],
        montant: i === 3 ? reste - (montantMensuel * 2) : montantMensuel,
        observations: `Échéance ${i}/3`
      });
    }
    
    setEcheancierForm({
      type_echeancier: 'mensuel',
      echeances: echeances
    });
    setShowEcheancierModal(true);
  };
  
  // Ajouter une échéance au formulaire
  const addEcheanceToForm = () => {
    setEcheancierForm(prev => ({
      ...prev,
      echeances: [...prev.echeances, { date: '', montant: '', observations: '' }]
    }));
  };
  
  // Supprimer une échéance du formulaire
  const removeEcheanceFromForm = (index) => {
    setEcheancierForm(prev => ({
      ...prev,
      echeances: prev.echeances.filter((_, i) => i !== index)
    }));
  };
  
  // Créer l'échéancier
  const handleCreateEcheancier = async () => {
    if (!selectedDocument) return;
    
    // Valider les échéances
    const validEcheances = echeancierForm.echeances.filter(e => e.date && e.montant);
    if (validEcheances.length === 0) {
      toast.error('Veuillez ajouter au moins une échéance valide');
      return;
    }
    
    setCreatingEcheancier(true);
    try {
      await axios.post(
        `${API}/souscriptions/admin/${selectedDocument.id}/echeancier`,
        {
          type_echeancier: echeancierForm.type_echeancier,
          echeances: validEcheances.map(e => ({
            date: e.date,
            montant: parseFloat(e.montant),
            observations: e.observations
          }))
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Échéancier créé avec ${validEcheances.length} échéance(s)`);
      setShowEcheancierModal(false);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingEcheancier(false);
    }
  };

  const _normalizeMode = (raw) => {
    const v = String(raw || '').toLowerCase().trim();
    const map = {
      vir: 'virement', virement: 'virement',
      sepa: 'sepa_core', sepa_core: 'sepa_core', sepa_b2b: 'sepa_b2b',
      cb: 'cb', stripe: 'cb',
      chq: 'cheque', cheque: 'cheque', 'chèque': 'cheque',
      esp: 'especes', especes: 'especes', 'espèces': 'especes',
    };
    return map[v] || (['virement','sepa_core','sepa_b2b','cb','cheque','especes'].includes(v) ? v : 'virement');
  };

  // Ouvrir le modal d'édition d'acompte
  const openEditAcompteModal = (doc, acompte, index) => {
    setSelectedDocument(doc);
    setSelectedAcompte(acompte);
    setAcompteForm({
      montant: acompte.amount || acompte.montant || '',
      date: acompte.date?.split('T')[0] || '',
      mode: _normalizeMode(acompte.method || acompte.mode_paiement),
      reference: acompte.reference || '',
      isScheduled: acompte.is_scheduled || false,
      scheduledDate: acompte.scheduled_date?.split('T')[0] || ''
    });
    setSelectedPaiementIndex(index);
    setShowEditAcompteModal(true);
  };

  // Sauvegarder les modifications d'acompte
  const saveAcompteModification = async () => {
    if (!selectedDocument || selectedPaiementIndex === null) return;
    
    try {
      await axios.put(
        `${API}/souscriptions/admin/${selectedDocument.id}/paiement/${selectedPaiementIndex}`,
        {
          montant: parseFloat(acompteForm.montant),
          date: acompteForm.date,
          mode_paiement: acompteForm.mode,
          reference: acompteForm.reference,
          is_scheduled: acompteForm.isScheduled,
          scheduled_date: acompteForm.scheduledDate || null
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Acompte modifié avec succès');
      setShowEditAcompteModal(false);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // Annuler un acompte
  const cancelAcompte = async (doc, index) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cet acompte ?')) return;
    
    try {
      await axios.delete(
        `${API}/souscriptions/admin/${doc.id}/paiement/${index}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Acompte annulé');
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterMontantMin('');
    setFilterMontantMax('');
    setFilterRecuStatus('all');
    setFilterDateDebut('');
    setFilterDateFin('');
  };

  return {
    _normalizeMode, addEcheanceToForm, cancelAcompte, handleCreateEcheancier, handlePartialPayment, handleUpdateEcheance,
    handleValidatePayment, loadPartialPaymentsHistory, openEcheanceModal, openEcheancierModal, openEditAcompteModal, openPartialPaymentModal,
    openValidationModal, removeEcheanceFromForm, resetFilters, saveAcompteModification,
  };
}
